import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Mail, 
  Search,
  Download,
  Building2,
  UserPlus,
  CheckCircle,
  Clock
} from "lucide-react";
import { format } from "date-fns";

interface PlatformContact {
  id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  source: string;
  status: string;
  total_invites_received: number;
  first_seen_at: string;
  last_contacted_at: string | null;
  organization_id: string | null;
  organization?: { name: string; slug: string } | null;
}

const statusConfig: Record<string, { color: string; label: string }> = {
  lead: { color: "bg-gray-100 text-gray-800", label: "Lead" },
  invited: { color: "bg-blue-100 text-blue-800", label: "Invited" },
  registered: { color: "bg-green-100 text-green-800", label: "Registered" },
  active: { color: "bg-emerald-100 text-emerald-800", label: "Active" },
};

const sourceConfig: Record<string, { label: string }> = {
  team_invite: { label: "Team Invite" },
  team_invite_create: { label: "Team Lead Invite" },
  event_invite: { label: "Event Invite" },
  org_invite: { label: "Org Invite" },
};

const SuperAdminContacts: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [orgFilter, setOrgFilter] = useState<string>("all");

  // Fetch organizations for filter
  const { data: organizations } = useQuery({
    queryKey: ["organizations-list"],
    queryFn: async () => {
      const { data } = await supabase
        .from("organizations")
        .select("id, name, slug")
        .eq("is_active", true)
        .order("name");
      return data || [];
    },
  });

  const { data: contacts, isLoading } = useQuery({
    queryKey: ["super-admin-contacts", search, statusFilter, sourceFilter, orgFilter],
    queryFn: async () => {
      let query = supabase
        .from("platform_contacts")
        .select(`
          *,
          organization:organizations!organization_id(name, slug)
        `)
        .order("created_at", { ascending: false });

      if (search) {
        query = query.or(`email.ilike.%${search}%,name.ilike.%${search}%`);
      }

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (sourceFilter !== "all") {
        query = query.eq("source", sourceFilter);
      }

      if (orgFilter !== "all") {
        query = query.eq("organization_id", orgFilter);
      }

      const { data, error } = await query.limit(200);

      if (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }

      return (data || []) as PlatformContact[];
    },
  });

  // Calculate stats
  const stats = React.useMemo(() => {
    if (!contacts) return { total: 0, leads: 0, invited: 0, registered: 0 };
    return {
      total: contacts.length,
      leads: contacts.filter(c => c.status === 'lead').length,
      invited: contacts.filter(c => c.status === 'invited').length,
      registered: contacts.filter(c => ['registered', 'active'].includes(c.status)).length,
    };
  }, [contacts]);

  const handleExport = () => {
    if (!contacts || contacts.length === 0) return;

    const csv = [
      ["Email", "First Name", "Last Name", "Phone", "Organization", "Source", "Status", "Invites", "First Seen", "Last Contacted"].join(","),
      ...contacts.map(c => [
        c.email,
        c.first_name || "",
        c.last_name || "",
        c.phone || "",
        c.organization?.name || "No Org",
        sourceConfig[c.source]?.label || c.source,
        statusConfig[c.status]?.label || c.status,
        c.total_invites_received,
        format(new Date(c.first_seen_at), "yyyy-MM-dd"),
        c.last_contacted_at ? format(new Date(c.last_contacted_at), "yyyy-MM-dd") : "",
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all-contacts-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">All Contacts</h1>
          <p className="text-slate-600">
            Global contact database across all organizations
          </p>
        </div>
        <Button onClick={handleExport} variant="outline" disabled={!contacts?.length}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-slate-600">
              <Users className="h-4 w-4" />
              Total Contacts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-slate-600">
              <UserPlus className="h-4 w-4" />
              Leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{stats.leads}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-slate-600">
              <Clock className="h-4 w-4" />
              Invited
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.invited}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="h-4 w-4" />
              Registered
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.registered}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800">Global Contact Database</CardTitle>
          <CardDescription className="text-slate-600">
            Search and filter contacts across all organizations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Organization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {organizations?.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="registered">Registered</SelectItem>
                <SelectItem value="active">Active</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="team_invite">Team Invite</SelectItem>
                <SelectItem value="team_invite_create">Team Lead Invite</SelectItem>
                <SelectItem value="event_invite">Event Invite</SelectItem>
                <SelectItem value="org_invite">Org Invite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {contacts && contacts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invites</TableHead>
                  <TableHead>First Seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-slate-800">
                          {contact.first_name || contact.name || "—"}
                          {contact.last_name && ` ${contact.last_name}`}
                        </div>
                        <div className="text-sm text-slate-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {contact.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.organization ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">{contact.organization.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">No org</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {sourceConfig[contact.source]?.label || contact.source}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[contact.status]?.color || "bg-gray-100"}>
                        {statusConfig[contact.status]?.label || contact.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{contact.total_invites_received}</span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {format(new Date(contact.first_seen_at), "MMM d, yyyy")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800">No contacts yet</h3>
              <p className="text-slate-500">
                Contacts will appear here when users send invitations.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminContacts;